import React, { createContext, useContext, ReactNode } from 'react';
import { useSubscriptionLimit } from '../../hooks/useSubscriptionLimit';
import UpgradeLimitModal from './upgrade-limit-modal';

type LimitType = 'companies' | 'collaborators' | 'services' | 'promoCodes';

interface SubscriptionLimitContextType {
  checkCompanyLimit: () => Promise<boolean>;
  checkCollaboratorLimit: () => Promise<boolean>;
  triggerLimitError: (limitType: LimitType, currentCount: number, limit: number) => void;
}

const SubscriptionLimitContext = createContext<SubscriptionLimitContextType | undefined>(undefined);

interface SubscriptionLimitProviderProps {
  children: ReactNode;
}

/**
 * Provider qui enveloppe l'application et gère automatiquement
 * l'affichage des modals de limites d'abonnement.
 * Ferme automatiquement tous les modals ouverts quand une limite est détectée.
 */
export const SubscriptionLimitProvider: React.FC<SubscriptionLimitProviderProps> = ({ children }) => {
  const { limitState, checkCompanyLimit, checkCollaboratorLimit, triggerLimitError, closeModal } = useSubscriptionLimit();

  return (
    <SubscriptionLimitContext.Provider
      value={{
        checkCompanyLimit,
        checkCollaboratorLimit,
        triggerLimitError
      }}
    >
      {children}

      {/* Modal qui s'affiche automatiquement quand une limite est atteinte */}
      {limitState.showModal && limitState.limitType && (
        <UpgradeLimitModal
          isOpen={limitState.showModal}
          onClose={closeModal}
          limitType={limitState.limitType}
          currentCount={limitState.currentCount}
          limit={limitState.limit}
        />
      )}
    </SubscriptionLimitContext.Provider>
  );
};

/**
 * Hook pour utiliser les vérifications de limites dans n'importe quel composant
 */
export const useSubscriptionLimitContext = () => {
  const context = useContext(SubscriptionLimitContext);
  if (!context) {
    throw new Error('useSubscriptionLimitContext must be used within SubscriptionLimitProvider');
  }
  return context;
};
